// Engineering site contact form — posts to /api/contact/submit and lands in the
// Super Admin inbox (contact_submissions collection).  Reused on the main
// Engineering landing (#contact) and on every service detail page (pre-fills
// the service so the admin sees exactly which page produced the lead).
import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertTriangle } from 'lucide-react';
import api from '@/lib/api';
import { formatApiError } from '@/lib/auth';
import { ENGINEERING_BRAND } from '@/lib/publicSiteContent';

export default function EngineeringContactForm({ serviceSlug = null, serviceTitle = null, id = 'contact' }) {
  const b = ENGINEERING_BRAND;
  const [form, setForm] = useState({
    name: '', email: '', phone: '', organization: '',
    message: serviceTitle ? `Enquiry about ${serviceTitle}: ` : '',
    company_url: '', // honeypot
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        organization: form.organization.trim() || null,
        message: form.message.trim(),
        modules: ['engineering' + (serviceSlug ? `:${serviceSlug}` : '')],
        tools_interest: serviceTitle ? `Engineering service · ${serviceTitle}` : 'Engineering enquiry',
        company_url: form.company_url,
      };
      await api.post('/contact/submit', payload);
      setDone(true);
      setForm({ name: '', email: '', phone: '', organization: '', message: '', company_url: '' });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id={id} className="py-20" style={{ background: b.primary, color: '#FFFFFF' }} data-testid={`engineering-contact-form-${serviceSlug || 'main'}`}>
      <div className="container-x">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold mb-3" style={{ color: b.accent }}>Request a service</div>
          <h2 className="font-display font-bold text-3xl lg:text-4xl mb-4 leading-tight">
            {serviceTitle ? `Enquire about ${serviceTitle}` : 'Ready to engage our engineering team?'}
          </h2>
          <p className="text-base" style={{ color: 'rgba(255,255,255,0.72)' }}>
            Send us your scope through the form below or reach us directly. Every submission lands in our admin inbox and we respond within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-6xl mx-auto">
          {/* Contact details column */}
          <div className="lg:col-span-4 space-y-3">
            <ContactRow icon={Mail} label="Email" value={b.email} href={`mailto:${b.email}`} accent={b.accent} />
            <ContactRow icon={Phone} label="Phone" value={b.phone} href={`tel:${b.phone.replace(/\s+/g, '')}`} accent={b.accent} />
            <ContactRow icon={MapPin} label="Location" value={b.address} accent={b.accent} />
            <div className="p-4 rounded-md border" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
              <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-1" style={{ color: b.accent }}>Response time</div>
              <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>All engineering enquiries are reviewed within 24 hours (Mon–Fri, WAT).</p>
            </div>
          </div>

          {/* Form column */}
          <div className="lg:col-span-8">
            {done ? (
              <div className="p-8 rounded-md border text-center" style={{ background: 'rgba(255,255,255,0.05)', borderColor: `${b.accent}55` }} data-testid="engineering-contact-success">
                <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center mb-4" style={{ background: `${b.accent}25`, color: b.accent }}>
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <h3 className="font-display font-bold text-xl mb-2">Thank you — we have received your enquiry.</h3>
                <p className="text-sm max-w-md mx-auto" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Your message has been forwarded to our engineering team. Expect a structured response within 24 business hours.
                </p>
                <button onClick={() => setDone(false)} className="mt-5 text-xs uppercase tracking-wider font-semibold" style={{ color: b.accent }}>
                  Submit another enquiry →
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="p-6 lg:p-8 rounded-md border" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }} data-testid="engineering-contact-form">
                {/* Honeypot */}
                <input type="text" name="company_url" value={form.company_url} onChange={change('company_url')} autoComplete="off" tabIndex={-1} className="hidden" aria-hidden />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField label="Full name" required>
                    <input required type="text" value={form.name} onChange={change('name')} className={inputCls} data-testid="engineering-contact-name" placeholder="Your name" />
                  </FormField>
                  <FormField label="Email address" required>
                    <input required type="email" value={form.email} onChange={change('email')} className={inputCls} data-testid="engineering-contact-email" placeholder="you@example.com" />
                  </FormField>
                  <FormField label="Phone number">
                    <input type="tel" value={form.phone} onChange={change('phone')} className={inputCls} data-testid="engineering-contact-phone" placeholder="+234 ..." />
                  </FormField>
                  <FormField label="Organization / school">
                    <input type="text" value={form.organization} onChange={change('organization')} className={inputCls} data-testid="engineering-contact-org" placeholder="Optional" />
                  </FormField>
                </div>

                {serviceTitle && (
                  <div className="mt-4 p-3 rounded-md text-sm" style={{ background: `${b.accent}18`, color: '#FFFFFF', border: `1px solid ${b.accent}45` }}>
                    <span className="text-xs uppercase tracking-wider font-semibold mr-2" style={{ color: b.accent }}>Service:</span>
                    {serviceTitle}
                  </div>
                )}

                <FormField label="Describe your scope, timeline or issue" required className="mt-4">
                  <textarea required rows={5} value={form.message} onChange={change('message')} className={inputCls} data-testid="engineering-contact-message" placeholder="Tell us what you need — devices affected, site location, urgency..." />
                </FormField>

                {error && (
                  <div className="mt-3 p-3 rounded-md text-sm flex items-start gap-2" style={{ background: 'rgba(220,38,38,0.15)', color: '#FCA5A5' }}>
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="mt-5 w-full px-5 py-3 rounded-md font-semibold text-sm inline-flex items-center justify-center gap-2 transition-transform hover:scale-[1.01] disabled:opacity-60"
                  style={{ background: b.accent, color: b.primary }}
                  data-testid="engineering-contact-submit"
                >
                  <Send className="w-4 h-4" />
                  {busy ? 'Sending…' : 'Send enquiry'}
                </button>

                <p className="mt-3 text-[11px] leading-relaxed text-center" style={{ color: 'rgba(255,255,255,0.5)' }}>
                  Your enquiry is delivered straight to the ATTP NEXUS Super Admin inbox. We do not share your details.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const inputCls = 'w-full px-3.5 py-2.5 rounded-md text-sm bg-white/5 border border-white/15 text-white placeholder:text-white/40 focus:outline-none focus:border-white/40 focus:bg-white/10 transition-colors';

function FormField({ label, required, children, className = '' }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs uppercase tracking-wider font-semibold text-white/70 mb-1.5">
        {label}{required && <span className="ml-1" style={{ color: ENGINEERING_BRAND.accent }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function ContactRow({ icon: Icon, label, value, href, accent }) {
  const content = (
    <div className="p-4 rounded-md border flex items-start gap-3 group transition-colors hover:bg-white/5" style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
      <div className="h-10 w-10 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${accent}20`, color: accent }}>
        <Icon className="w-4.5 h-4.5" />
      </div>
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold mb-0.5" style={{ color: accent }}>{label}</div>
        <div className="text-sm text-white/95 break-all">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href}>{content}</a> : content;
}
